import { Link } from "wouter";
import { ArrowRight, Check, MapPin, Clock, Truck, Shield, Zap, Target } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead, { createLocalBusinessSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES, CONTACT } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";

const localBenefits = [
  {
    icon: Truck,
    title: "Snabb leverans till Norrköping",
    text: "Vi planerar leveranser från Eskilstuna till Norrköpings logistik- och industriområden utifrån projektets omfattning och önskat datum.",
  },
  {
    icon: Clock,
    title: "Korta ledtider",
    text: "Tack vare tre CO2-lasrar och hög kapacitet kan vi hantera brådskande beställningar från Norrköpingsregionen med kort varsel.",
  },
  {
    icon: Shield,
    title: "Garanterad precision",
    text: "Toleranser ner till 0,1 mm med moderna Trumpf- och Amada CO2-lasrar. Första bit-kontroll vid varje projektstart.",
  },
  {
    icon: Target,
    title: "Helhetslösning",
    text: "Kombinera laserskärning med kantpressning och svetsning – allt under ett tak. Färdiga komponenter levererade till Norrköping.",
  },
];

const norrkopingIndustries = [
  {
    name: "Logistik & Transport",
    desc: "Norrköping är ett logistiknav. Vi levererar komponenter till transportsektorn och materialhanteringssystem.",
  },
  {
    name: "Tech & Visualisering",
    desc: "Från Visualiseringscenter C till tech-startups, vi tillverkar chassin, prototyper och specialkomponenter.",
  },
  {
    name: "Papper & Förpackning",
    desc: "Med anor i pappersindustrin levererar vi reservdelar och komponenter till maskiner inom pappers- och förpackningsindustrin.",
  },
  {
    name: "Industri & Produktion",
    desc: "Prototyper och serieproduktion för tillverkande företag i Norrköpingsområdet. Från enstaka detaljer till stora volymer.",
  },
];

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
];

export default function LaserskarningNorrkoping() {
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Laserskärning Norrköping",
    description:
      "Professionell laserskärning för företag i Norrköping. ASPAB erbjuder CO2 Laser-skärning med toleranser ner till 0,1 mm, snabba leveranser och brett materialutbud.",
    provider: {
      "@type": "LocalBusiness",
      name: "ASPAB",
      telephone: "+46161326 00",
      email: "order@aspab.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Abramsons väg 2",
        addressLocality: "Eskilstuna",
        postalCode: "632 39",
        addressCountry: "SE",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Norrköping",
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    serviceType: "Laserskärning",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Laserskärningstjänster",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning stål",
            description: "Laserskärning i svart stål upp till 25 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning rostfritt",
            description: "Laserskärning i rostfritt stål upp till 20 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning aluminium",
            description: "Laserskärning i aluminium upp till 15 mm tjocklek",
          },
        },
      ],
    },
  };

  const localBusinessSchema = createLocalBusinessSchema({
    areaServed: "Norrköping",
    serviceDescription: "Professionell laserskärning, kantpressning och svetsning för företag i Norrköping. Leveransplan bekräftas i offerten.",
  });

  return (
    <Layout>
      <SEOHead
        title="Laserskärning Norrköping – För tech & logistik | ASPAB"
        description="Laserskärning för Norrköpings tech- och logistikföretag. ASPAB levererar med 0,1 mm tolerans. Stål, rostfritt & aluminium. Begär offert!"
        canonical="https://aspab.com/laserskarning-norrkoping"
        path="/laserskarning-norrkoping"
        keywords="laserskärning Norrköping, plåtbearbetning Norrköping, metallbearbetning Norrköping, laserskärning Östergötland"
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Norrköping", href: "/laserskarning-norrkoping" },
        ]}
        structuredData={[localServiceSchema, localBusinessSchema]}
        ogImage={IMAGES.ogNorrkoping}
      />

      <PageHero
        title="Laserskärning Norrköping"
        subtitle="Specialiserad laserskärning för Norrköpings industri, från tech till logistik. Precision och snabbhet från ASPAB i Eskilstuna."
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Norrköping", href: "/laserskarning-norrkoping" },
        ]}
        image={IMAGES.heroLaser}
      />

      {/* Intro – Varför välja ASPAB från Norrköping */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Laserskärning för Norrköpingsregionen
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Din partner för laserskärning i Norrköping
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Från vår verkstad i Eskilstuna är vi en effektiv partner för laserskurna detaljer till företag i Norrköping.
                  Stadens arv inom papper och textil har utvecklats till en modern hub för tech och logistik, och vi är redo att möta era behov.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi levererar till allt från startups inom visualisering till stora logistikaktörer. Oavsett om ni behöver prototyper för en ny produkt eller serietillverkade komponenter för era system, garanterar vi precision och pålitliga leveranser.
                </p>
                <div className="flex items-center gap-3 mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" />
                  <div>
                    <span className="text-gray-900 font-medium text-sm">Leverans från ASPAB i Eskilstuna till Norrköping</span>
                    <span className="text-gray-500 text-sm block">Regelbundna leveranser till Östergötland</span>
                  </div>
                </div>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Begär offert – fri leveransbedömning <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <LazyImage
                  src={IMAGES.laserOldSite}
                  alt="Laserskärning för företag i Norrköping – ASPAB CO2 Laser"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">0,1 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Tolerans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">&lt;48h</div>
                    <div className="text-gray-500 text-sm mt-1">Leverans enkla detaljer</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">25 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Max tjocklek stål</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">30+</div>
                    <div className="text-gray-500 text-sm mt-1">Års erfarenhet</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Fördelar med ASPAB för Norrköpingsföretag */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Fördelar
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Varför Norrköpingsföretag väljer ASPAB
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Kapacitet, specialistkunskap och pålitliga leveranser. Vi är en komplett partner för plåtbearbetning.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {localBenefits.map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 h-full">
                  <item.icon className="w-8 h-8 text-[#2563EB]" />
                  <h3 className="font-heading text-lg font-bold text-gray-900 mt-4 mb-2 tracking-tight">{item.title}</h3>
                  <p className="text-gray-500 text-sm">{item.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Branscher i Norrköping */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Branscher vi servar i Norrköping
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Från logistik till visualisering
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Vår breda kapacitet inom laserskärning gör oss till en värdefull partner för många av Norrköpings nyckelindustrier.
                </p>
                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  {norrkopingIndustries.map((industry, i) => (
                    <div key={i} className="bg-gray-50 border border-gray-100 rounded-lg p-5">
                      <h4 className="font-heading text-md font-bold text-gray-900">{industry.name}</h4>
                      <p className="text-gray-500 text-sm mt-1">{industry.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.laserVideo}
                alt="Laserskärning av stålplåt med CO2 Laser hos ASPAB"
                className="w-full rounded-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Kapabiliteter */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14 mx-auto text-center">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase_">
                Kapacitet
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Material och tjocklekar
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Vi skär i de flesta metalliska material. Nedan är ett urval av våra vanligaste material och maximala tjocklekar för laserskärning.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full divide-y-2 divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3 font-medium text-gray-900 text-left">Material</th>
                    <th className="whitespace-nowrap px-4 py-3 font-medium text-gray-900 text-left">Max tjocklek</th>
                    <th className="whitespace-nowrap px-4 py-3 font-medium text-gray-900 text-left">Min tjocklek</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {capabilities.map((cap, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">{cap.material}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700">{cap.maxThickness}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700">{cap.minThickness}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14 mx-auto text-center">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Process
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Från ritning till leverans i Norrköping
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Vår process är optimerad för snabbhet och kvalitet, från första kontakt till färdig produkt.
              </p>
            </div>
          </AnimatedSection>

          <div className="relative">
            <div
              className="absolute left-1/2 -translate-x-1/2 top-4 bottom-4 w-px bg-gray-200 hidden md:block"
              aria-hidden="true"
            ></div>

            <div className="space-y-12 md:space-y-16">
              {/* Step 1 */}
              <AnimatedSection className="md:grid md:grid-cols-2 md:gap-12 items-center">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 border-2 border-white ring-2 ring-gray-200 flex items-center justify-center font-bold text-gray-500 text-lg shrink-0">
                    1
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-gray-900">Ritning & Offert</h3>
                </div>
                <div className="md:ml-[76px] mt-4 md:mt-0">
                  <p className="text-gray-500">
                    Skicka in dina ritningar (DXF, DWG, STEP) via vårt kontaktformulär. Vi återkommer snabbt med en offert baserad på material, tidsåtgång och antal.
                  </p>
                </div>
              </AnimatedSection>

              {/* Step 2 */}
              <AnimatedSection className="md:grid md:grid-cols-2 md:gap-12 items-center">
                <div className="flex items-center gap-4 md:flex-row-reverse md:text-right">
                  <div className="w-12 h-12 rounded-full bg-gray-100 border-2 border-white ring-2 ring-gray-200 flex items-center justify-center font-bold text-gray-500 text-lg shrink-0">
                    2
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-gray-900">Beredning & Produktion</h3>
                </div>
                <div className="md:mr-[76px] mt-4 md:mt-0 md:text-right">
                  <p className="text-gray-500">
                    Efter godkänd offert förbereder våra tekniker filerna för produktion och planerar in jobbet i en av våra fyra lasermaskiner.
                  </p>
                </div>
              </AnimatedSection>

              {/* Step 3 */}
              <AnimatedSection className="md:grid md:grid-cols-2 md:gap-12 items-center">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 border-2 border-white ring-2 ring-gray-200 flex items-center justify-center font-bold text-gray-500 text-lg shrink-0">
                    3
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-gray-900">Kvalitetskontroll</h3>
                </div>
                <div className="md:ml-[76px] mt-4 md:mt-0">
                  <p className="text-gray-500">
                    Varje skuren detalj genomgår en noggrann kvalitetskontroll. Vi säkerställer att toleranser och ytkrav uppfylls enligt specifikation.
                  </p>
                </div>
              </AnimatedSection>

              {/* Step 4 */}
              <AnimatedSection className="md:grid md:grid-cols-2 md:gap-12 items-center">
                <div className="flex items-center gap-4 md:flex-row-reverse md:text-right">
                  <div className="w-12 h-12 rounded-full bg-gray-100 border-2 border-white ring-2 ring-gray-200 flex items-center justify-center font-bold text-gray-500 text-lg shrink-0">
                    4
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-gray-900">Leverans till Norrköping</h3>
                </div>
                <div className="md:mr-[76px] mt-4 md:mt-0 md:text-right">
                  <p className="text-gray-500">
                    Färdiga detaljer packas och skickas med egna bilar eller speditör. Vi levererar direkt till din anläggning i Norrköping.
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Relaterade tjänster */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Fler tjänster
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  En komplett partner inom plåtbearbetning
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Utöver laserskärning erbjuder vi en rad andra tjänster för att kunna leverera kompletta lösningar. Allt utförs i vår egen verkstad i Eskilstuna.
                </p>
                <div className="space-y-4 mt-8">
                  <div className="flex items-start gap-4">
                    <Check className="w-6 h-6 text-green-500 shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Kantpressning</h4>
                      <p className="text-gray-500 text-sm">Bockning av plåtdetaljer med hög precision i moderna kantpressar.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-6 h-6 text-green-500 shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Svetsning</h4>
                      <p className="text-gray-500 text-sm">Svetsning i stål, rostfritt och aluminium.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Check className="w-6 h-6 text-green-500 shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Tre CO2-lasrar</h4>
                      <p className="text-gray-500 text-sm">Hög kapacitet för både små och stora serier med snabba ledtider.</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="grid grid-cols-2 gap-4">
                <LazyImage
                  src={IMAGES.svetsningOldSite}
                  alt="Svetsning av stålkonstruktion"
                  className="w-full h-full object-cover rounded-lg"
                />
                <LazyImage
                  src={IMAGES.workshop3}
                  alt="Kantpressning av plåtdetalj"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container text-center max-w-3xl mx-auto">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
              Redo att starta ditt projekt i Norrköping?
            </h2>
            <p className="text-gray-500 mt-4 text-lg leading-relaxed">
              Skicka in din förfrågan så återkommer vi med en kostnadsfri offert. Vi ser fram emot att bli din partner för laserskärning i Norrköping.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <Link
                href="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors"
              >
                Begär offert <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/om-oss"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-800 font-semibold rounded-md hover:bg-gray-200 transition-colors"
              >
                Läs mer om oss
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
